/**
 * Defensive Web Storage helpers.
 *
 * All access is wrapped in try/catch and returns a safe fallback instead of
 * throwing. This prevents the class of crash seen when iOS Safari has
 * "Block All Cookies" enabled (localStorage access throws SecurityError).
 *
 * These helpers must only be called from client code (event handlers or
 * effects) — never during render on the server.
 */

function probe(store: 'localStorage' | 'sessionStorage'): boolean {
    try {
        const k = '__fintek_probe__';
        window[store].setItem(k, k);
        window[store].removeItem(k);
        return true;
    } catch {
        return false;
    }
}

/** True only when localStorage is present AND writable. */
export function storageAvailable(): boolean {
    if (typeof window === 'undefined') return false;
    return probe('localStorage');
}

export const safeStorage = {
    get(key: string): string | null {
        try {
            return window.localStorage.getItem(key);
        } catch {
            return null;
        }
    },
    /** Returns true on success, false when the write was blocked/failed. */
    set(key: string, value: string): boolean {
        try {
            window.localStorage.setItem(key, value);
            return true;
        } catch {
            return false;
        }
    },
    remove(key: string): void {
        try {
            window.localStorage.removeItem(key);
        } catch {
            /* ignore */
        }
    },
};

export const safeSessionStorage = {
    get(key: string): string | null {
        try {
            return window.sessionStorage.getItem(key);
        } catch {
            return null;
        }
    },
    set(key: string, value: string): boolean {
        try {
            window.sessionStorage.setItem(key, value);
            return true;
        } catch {
            return false;
        }
    },
    remove(key: string): void {
        try {
            window.sessionStorage.removeItem(key);
        } catch {
            /* ignore */
        }
    },
};
